<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AiKnowledgeSourcesAPI from 'dashboard/api/aiKnowledgeSources';

const props = defineProps({
  enabledSourceIds: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:enabledSourceIds']);

const { t } = useI18n();
const sources = ref([]);
const isLoading = ref(false);
const draft = ref({
  name: '',
  source_type: 'website_url',
  source_url: '',
});
const selectedFile = ref(null);

const loadSources = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiKnowledgeSourcesAPI.get();
    sources.value = (data || []).map(source => ({
      ...source,
      is_enabled: Boolean(props.enabledSourceIds[source.id]),
    }));
  } finally {
    isLoading.value = false;
  }
};

const onToggle = source => {
  const next = { ...props.enabledSourceIds, [source.id]: source.is_enabled };
  emit('update:enabledSourceIds', next);
};

const onFileChange = event => {
  const [file] = event.target.files || [];
  selectedFile.value = file || null;
};

const addSource = async () => {
  if (!draft.value.name.trim()) return;

  try {
    if (draft.value.source_type === 'file') {
      if (!selectedFile.value) return;
      await AiKnowledgeSourcesAPI.create(
        { name: draft.value.name, source_type: 'file' },
        selectedFile.value
      );
    } else {
      await AiKnowledgeSourcesAPI.create({
        name: draft.value.name,
        source_type: 'website_url',
        source_url: draft.value.source_url,
      });
    }

    draft.value = { name: '', source_type: 'website_url', source_url: '' };
    selectedFile.value = null;
    useAlert(t('AI_AGENTS.API.UPDATE_SUCCESS'));
    await loadSources();
  } catch {
    useAlert(t('AI_AGENTS.API.ERROR'));
  }
};

onMounted(loadSources);
</script>

<template>
  <section class="grid gap-4">
    <div
      class="rounded-lg border border-n-amber-3 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
    >
      {{ t('AI_AGENTS.TEMPLATE_PREVIEW.KNOWLEDGE_TIP') }}
    </div>

    <div class="grid gap-3 p-4 border rounded-lg border-n-weak">
      <Input
        v-model="draft.name"
        :label="t('AI_AGENTS.KNOWLEDGE.SOURCE_NAME')"
      />
      <label class="grid gap-1 text-sm">
        <span class="text-n-slate-11">{{
          t('AI_AGENTS.KNOWLEDGE.SOURCE_TYPE')
        }}</span>
        <select
          v-model="draft.source_type"
          class="h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option value="website_url">
            {{ t('AI_AGENTS.KNOWLEDGE.TYPE_WEBSITE') }}
          </option>
          <option value="file">{{ t('AI_AGENTS.KNOWLEDGE.TYPE_FILE') }}</option>
        </select>
      </label>
      <Input
        v-if="draft.source_type === 'website_url'"
        v-model="draft.source_url"
        :label="t('AI_AGENTS.KNOWLEDGE.SOURCE_URL')"
      />
      <label v-else class="grid gap-1 text-sm">
        <span class="text-n-slate-11">{{
          t('AI_AGENTS.KNOWLEDGE.SOURCE_FILE')
        }}</span>
        <input
          type="file"
          accept=".pdf,.txt,.md,.csv,.json,text/plain,application/pdf"
          class="text-sm text-n-slate-12"
          @change="onFileChange"
        />
      </label>
      <Button
        :label="t('AI_AGENTS.KNOWLEDGE.ADD_SOURCE')"
        icon="i-lucide-plus"
        size="sm"
        @click="addSource"
      />
    </div>

    <ul v-if="sources.length" class="grid gap-2 p-0 m-0 list-none">
      <li
        v-for="source in sources"
        :key="source.id"
        class="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-lg border-n-weak"
      >
        <div class="grid gap-1 min-w-0">
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ source.name }}
          </p>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              source.source_type === 'file'
                ? source.file_name || t('AI_AGENTS.KNOWLEDGE.TYPE_FILE')
                : source.source_url
            }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-11">
            {{ t('AI_AGENTS.KNOWLEDGE.USE_FOR_AGENT') }}
          </span>
          <Switch v-model="source.is_enabled" @change="onToggle(source)" />
        </div>
      </li>
    </ul>
    <p v-else-if="!isLoading" class="m-0 text-sm text-n-slate-11">
      {{ t('AI_AGENTS.KNOWLEDGE.EMPTY_ACCOUNT') }}
    </p>
  </section>
</template>
